import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { exchangeService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const DIRECTION_OPTIONS = [
  { value: '', label: 'Tutte' },
  { value: 'sent', label: 'Inviate' },
  { value: 'received', label: 'Ricevute' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Tutti' },
  { value: 'true', label: 'Attive' },
  { value: 'false', label: 'Non attive' },
];

const MyExchangeRequests = () => {
  const { currentUser } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState('');
  const [isActive, setIsActive] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (direction) params.direction = direction;
      if (isActive !== '') params.is_active = isActive;
      if (search.trim()) params.search = search.trim();
      const res = await exchangeService.my(params);
      setRequests(res.data || []);
    } catch {
      setError('Errore nel caricamento delle richieste.');
    } finally {
      setLoading(false);
    }
  }, [direction, isActive, search]);

  useEffect(() => {
    load();
  }, [load]);

  const resetFilters = () => {
    setSearch('');
    setDirection('');
    setIsActive('');
  };

  const handleRespond = useCallback(async (id, newStatus) => {
    try {
      await exchangeService.respond(id, newStatus);
      await load();
    } catch {
      setError('Errore nell\'aggiornamento della richiesta.');
    }
  }, [load]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  const getDirectionChip = (req) => {
    if (req.requester === currentUser?.id) {
      return <Chip label="Inviata" color="primary" size="small" variant="outlined" />;
    }
    return <Chip label="Ricevuta" color="secondary" size="small" variant="outlined" />;
  };

  const getStatusChip = (req) => {
    if (req.status === 'accepted') {
      return <Chip label="Accettata" color="success" size="small" variant="outlined" />;
    }
    if (req.status === 'rejected') {
      return <Chip label="Rifiutata" color="error" size="small" variant="outlined" />;
    }
    return <Chip label="In attesa" color="warning" size="small" variant="outlined" />;
  };

  const isReceivedPending = (req) =>
    req.recipient === currentUser?.id && req.status === 'pending';

  const sentCount = useMemo(() => requests.filter((r) => r.requester === currentUser?.id).length, [requests, currentUser]);
  const receivedCount = useMemo(() => requests.filter((r) => r.recipient === currentUser?.id).length, [requests, currentUser]);

  return (
    <Container component="main" maxWidth={false} disableGutters>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 4, md: 8 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3 }}>
          <Button
            component={Link}
            to="/dashboard"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            color="primary"
            sx={{ alignSelf: 'flex-start', mb: 1, textTransform: 'none' }}
          >
            Dashboard
          </Button>
          <Typography component="h1" variant="h4" gutterBottom sx={{ textAlign: { xs: 'left', sm: 'center' }, width: '100%' }}>
            Le mie richieste di scambio
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label={`Inviate: ${sentCount}`} color="primary" variant="outlined" />
            <Chip label={`Ricevute: ${receivedCount}`} color="secondary" variant="outlined" />
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Filtri */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center' }}>
          <TextField
            label="Cerca"
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Direzione</InputLabel>
            <Select value={direction} label="Direzione" onChange={(e) => setDirection(e.target.value)}>
              {DIRECTION_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Stato</InputLabel>
            <Select value={isActive} label="Stato" onChange={(e) => setIsActive(e.target.value)}>
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={resetFilters} sx={{ textTransform: 'none' }} size="small">
            Reset filtri
          </Button>
        </Box>

        {!isMobile ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Richiedente</TableCell>
                  <TableCell>Destinatario</TableCell>
                  <TableCell>Descrizione</TableCell>
                  <TableCell>Risposta</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography align="center" sx={{ py: 2 }}>Caricamento…</Typography>
                    </TableCell>
                  </TableRow>
                ) : requests.length > 0 ? (
                  requests.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{getDirectionChip(r)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.requester_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.requester_email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.recipient_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.recipient_email}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {r.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
                          {getStatusChip(r)}
                          {isReceivedPending(r) && (
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                              <Tooltip title="Approva">
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<CheckCircleOutlineIcon />}
                                  onClick={() => handleRespond(r.id, 'accepted')}
                                  sx={{ textTransform: 'none', px: 1 }}
                                >
                                  Approva
                                </Button>
                              </Tooltip>
                              <Tooltip title="Rifiuta">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<CancelOutlinedIcon />}
                                  onClick={() => handleRespond(r.id, 'rejected')}
                                  sx={{ textTransform: 'none', px: 1 }}
                                >
                                  Rifiuta
                                </Button>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(r.created_at)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography align="center" sx={{ py: 2 }} color="text.secondary">
                        Nessuna richiesta trovata.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : loading ? (
          <Typography align="center" sx={{ py: 3 }}>Caricamento…</Typography>
        ) : requests.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {requests.map((r) => (
              <Paper key={r.id} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {getDirectionChip(r)}
                  {getStatusChip(r)}
                </Box>
                <Typography variant="caption" color="text.secondary">Richiedente</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>{r.requester_name} — {r.requester_email}</Typography>
                <Typography variant="caption" color="text.secondary">Destinatario</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>{r.recipient_name} — {r.recipient_email}</Typography>
                <Typography variant="caption" color="text.secondary">Descrizione</Typography>
                <Typography variant="body2" sx={{ mb: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.description}</Typography>
                <Typography variant="caption" color="text.secondary">Data: {formatDate(r.created_at)}</Typography>
                {isReceivedPending(r) && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={() => handleRespond(r.id, 'accepted')}
                      sx={{ textTransform: 'none', flex: 1 }}
                    >
                      Approva
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<CancelOutlinedIcon />}
                      onClick={() => handleRespond(r.id, 'rejected')}
                      sx={{ textTransform: 'none', flex: 1 }}
                    >
                      Rifiuta
                    </Button>
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        ) : (
          <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
            Nessuna richiesta trovata.
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default MyExchangeRequests;
